import type { ComponentProps } from 'react';
import { forwardRef } from 'react';

import { generateDataTestId } from '@/lib/tests';

import * as S from './Button.styles';

export type ButtonVariant = 'primary' | 'outlined';
type ButtonSize = 'medium' | 'large';

export interface ButtonProps extends ComponentProps<'button'> {
  as?: React.ElementType;
  isFullWidth?: boolean;
  isFullWidthMobile?: boolean;
  isLoading?: boolean;
  isProcessing?: boolean;
  isUpperCase?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      isFullWidthMobile = true,
      isProcessing,
      isUpperCase = true,
      size = 'medium',
      type = 'button',
      variant = 'primary',
      ...props
    },
    ref,
  ) => {
    return (
      <S.Button
        $isFullWidthMobile={isFullWidthMobile}
        $isProcessing={isProcessing}
        $isUpperCase={isUpperCase}
        data-test-id='Button'
        size={size}
        type={type}
        variant={variant}
        {...generateDataTestId('button', type)}
        {...props}
        ref={ref}
      >
        {isProcessing && (
          <S.Loader data-test-id='ButtonLoader' size='sm' variant='contrast' />
        )}
        <S.Content data-test-id='ButtonContent' isProcessing={isProcessing}>
          {children}
        </S.Content>
      </S.Button>
    );
  },
);

Button.displayName = 'Button';
